import {
  Action,
  createAction,
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
} from '@reduxjs/toolkit';
import moment from 'moment';

import {
  AddressData,
  BillApi,
  ProductApi,
  EntityCustomShippingFeesApi,
  EntityDistanceShippingFeesApi,
  EntityLocationShippingFeesApi,
  AdminAddressApi,
} from 'api';
import { RootState } from 'app';
import { PROMOTION_TYPE } from 'containers/SaleManager/Discount/Discount/commons';
import {
  ADJUSTMENT_TYPE,
  compareAddress,
  convertAdditionalInfoAddressToAddressData,
  DELIVERY_FEE_TYPE,
  generateUUID,
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  SummaryBillType,
  ValidateBillConstraintsType,
} from 'utils';

export const updateBillSliceListenerMiddleware = createListenerMiddleware();

export const takeLastedCalcWholesaleProductItemWhenUpdateBill = createAction<
  any | undefined
>('takeLastedCalcWholesaleProductItemWhenUpdateBill');

updateBillSliceListenerMiddleware.startListening({
  actionCreator: takeLastedCalcWholesaleProductItemWhenUpdateBill,
  effect: async (action: Action, listenerApi: any) => {
    // cancel other running instances
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(800); // delay to wait next action before dispatching final one

    const { payload } = action as any;
    listenerApi.dispatch(
      checkBillItemQuantityForUpdateBill({
        body: payload,
        isUpdatingProductItem: true,
      })
    );
  },
});

export const updateBill = createAsyncThunk(
  'updateBill/updateBill',
  async (params: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await BillApi.updateBill(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findBillToUpdate = createAsyncThunk(
  'updateBill/findBillToUpdate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await BillApi.findBillToUpdate(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductForUpdateBill = createAsyncThunk<
  any,
  { id: string; body: any },
  { rejectValue: { message: string } }
>(
  'updateBill/findProductForUpdateBill',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductForUpdateBill(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForUpdateBill = createAsyncThunk(
  'updateBill/createAddressForUpdateBill',
  async (param: { id: string; body: AddressData }, { rejectWithValue }) => {
    try {
      const { id, body } = param;

      const response = await AdminAddressApi.create(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAddressForUpdateBill = createAsyncThunk<
  any,
  { id: string; body: AddressData },
  { rejectValue: { message: string } }
>(
  'updateBill/updateAddressForUpdateBill',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await AdminAddressApi.update(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkBillItemQuantityForUpdateBill = createAsyncThunk(
  'updateBill/checkBillItemQuantityForUpdateBill',
  async (
    params: { body: any; isUpdatingProductItem?: boolean },
    { rejectWithValue }
  ) => {
    const { body } = params;
    try {
      const response = await BillApi.checkBillItemQuantity(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findListAddressForUpdateBill = createAsyncThunk(
  'updateBill/findListAddressForUpdateBill',
  async (param: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = param;

      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityCustomShippingFeeToUpdateBill = createAsyncThunk(
  'entityCustomShippingFee/findEntityCustomShippingFeeToUpdateBill',
  async ({ billID }: { billID: string }, { rejectWithValue }) => {
    try {
      const response = await EntityCustomShippingFeesApi.findToUpdateBill(
        billID
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityDistanceShippingFeeToUpdateBill = createAsyncThunk(
  'entityDistanceShippingFee/findEntityDistanceShippingFeeToUpdateBill',
  async ({ billID }: { billID: string }, { rejectWithValue }) => {
    try {
      const response = await EntityDistanceShippingFeesApi.findToUpdateBill(
        billID
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeAddressForUpdateBill = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('updateBill/removeAddressForUpdateBill', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findEntityShippingPolicyItemToUpdateBill = createAsyncThunk(
  'entityShippingPolicyItem/findEntityShippingPolicyItemToUpdateBill',
  async ({ billID }: { billID: string }, { rejectWithValue }) => {
    try {
      const response = await EntityLocationShippingFeesApi.findToUpdateBill(
        billID
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);
interface IState {
  selectedProducts: any[];
  isWholesaleBill?: boolean;
  customerID: string;
  chainCustomerID: string;
  customerOwnerID: string;

  deliveryMethod: ORDER_DELIVERY_METHOD;
  paymentMethod: ORDER_PAYMENT_METHOD;
  address: AddressData | undefined;

  bankAccount: any | undefined;
  deliveryNote: any | undefined;
  images: any[];
  deliveryFee: number;
  deliveryFeeType: number;

  summaryBill: SummaryBillType;
  promotions?: any[];
  selectedOrderSalePromotionCondition?: any;

  validationResult: any;

  totalCustomerQuantity: number;

  isProductNotEnoughQuantity?: boolean;

  customerNote: string;
  supplierNote: string;

  addressData: any[];
  billID: string;

  billCode: string;
  bill: any;
  bills: any[];

  choiceItemList: any[];

  customer: any;
  supplier: any;
  partnerID: string | undefined;

  isLoading: boolean;

  shouldUpdateBillData: boolean;

  totalPrice: number;
  returnMoney: number;
  bankAccountID?: string;

  depositMoney?: {
    cash?: number;
    internetBanking?: number;
    creditCard?: number;
  };

  paidMoney?: {
    cash?: number;
    internetBanking?: number;
    creditCard?: number;
  };

  shouldCreateOrder?: boolean;

  deductedMoney?: number;
  excessMoney?: number;
  isDebt?: boolean;
  debtMoney?: number;
  createdAt?: string;
  oldEntityPricePolicyID?: string;
  entityPricePolicyID?: string;
  entityProposalPolicyID?: string;

  // tách dòng
  addQuantityValue: number;
  separateLine: boolean;

  // phí giao hàng
  isCheckingBillItemQuantity: boolean;
  isLoadingCustomShippingFees: boolean;
  customShippingFees?: any[];
  isLoadingDistanceShippingFees: boolean;
  distanceShippingFees?: any[];
  isLoadingShippingPolicyItems: boolean;
  shippingPolicyItems?: any[];
  locationShippingFee: any | undefined;
  distanceShippingFee: any | undefined;

  // check điều kiện cập nhật createdAt cho draft bill
  orderStatusUpdatedAt?: string;
}

const validationResultInitialState: ValidateBillConstraintsType = {
  isValid: true,
  // deleted proposal items
  isProductItemDeleted: false,
  deletedProductItemIDs: [],
  deletedBillItemIDs: [],
  // out of stock/ not enough proposal items
  isProductItemNotEnoughQuantity: false,
  notEnoughQuantityProductItems: [],
  notEnoughQuantityBillItems: [],
  deactivatedProductIDs: [],
  isProductDeactivated: false,
  // price-changed proposal items
  isProductItemPriceChanged: false,
  priceChangedProductItems: [],
  priceChangedBillItems: [],
  notEnoughQuantityProducts: [],
  isProductPriceChanged: false,
  priceChangedProducts: [],
  isAddressChanged: false,
  changedAddress: undefined,
};

const initialState: IState = {
  selectedProducts: [],
  customerID: '',
  chainCustomerID: '',
  customerOwnerID: '',
  billID: '',
  billCode: '',
  bill: {},
  customer: {},
  supplier: {},
  bills: [],

  deliveryMethod: ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY,
  paymentMethod: ORDER_PAYMENT_METHOD.CASH,
  address: undefined,

  bankAccount: undefined,
  deliveryNote: undefined,
  images: [],
  deliveryFee: 0,
  deliveryFeeType: 0,

  summaryBill: {
    debtMoney: BigInt(0),
    deductedMoney: BigInt(0),
    deliveryFeeType: 0,
    depositMoney: undefined,
    excessMoney: BigInt(0),
    isDebt: false,
    isValidBillReturnMoney: false,
    paidMoney: undefined,
    returnMoney: BigInt(0),
    totalDepositMoney: BigInt(0),
    totalPaidMoney: BigInt(0),
    deliveryFee: 0,
    totalPrice: BigInt(0),
    totalTemporaryPrice: BigInt(0),
    totalItem: 0,
    totalAdjustment: BigInt(0),
    adjustments: [],
  },
  promotions: [],

  validationResult: {},
  totalCustomerQuantity: 0,
  customerNote: '',
  supplierNote: '',
  addressData: [],
  choiceItemList: [],
  isWholesaleBill: undefined,
  isLoading: true,
  shouldUpdateBillData: false,
  partnerID: undefined,

  shouldCreateOrder: false,

  totalPrice: 0,
  returnMoney: 0,
  deductedMoney: 0,
  excessMoney: 0,
  isDebt: false,
  debtMoney: 0,
  createdAt: moment().toISOString(),

  bankAccountID: undefined,

  oldEntityPricePolicyID: undefined,
  entityPricePolicyID: undefined,
  entityProposalPolicyID: undefined,

  addQuantityValue: 1,
  separateLine: false,

  isCheckingBillItemQuantity: false,
  isLoadingCustomShippingFees: false,
  customShippingFees: [],
  isLoadingDistanceShippingFees: false,
  distanceShippingFees: [],
  isLoadingShippingPolicyItems: false,
  shippingPolicyItems: [],
  locationShippingFee: undefined,
  distanceShippingFee: undefined,

  orderStatusUpdatedAt: undefined,
};

const initialSummaryBill: SummaryBillType = {
  debtMoney: BigInt(0),
  deductedMoney: BigInt(0),
  deliveryFeeType: 0,
  depositMoney: undefined,
  excessMoney: BigInt(0),
  isDebt: false,
  isValidBillReturnMoney: false,
  paidMoney: undefined,
  returnMoney: BigInt(0),
  totalDepositMoney: BigInt(0),
  totalPaidMoney: BigInt(0),
  deliveryFee: 0,
  totalPrice: BigInt(0),
  totalTemporaryPrice: BigInt(0),
  totalItem: 0,
  totalAdjustment: BigInt(0),
  adjustments: [],
};

const updateBillSlice = createSlice({
  name: 'updateBill',
  initialState,
  reducers: {
    updateBillInfoToUpdateBill(state, { payload }) {
      const billInfo = payload;

      Object.keys(billInfo).forEach(key => {
        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = state.entityPricePolicyID || '';
          state.entityPricePolicyID = billInfo[key];
        }

        if (billInfo[key] != undefined) {
          state[key] = billInfo[key];
        }
      });
    },
    updateBankAccountInfoUpdateBillDataToUpdate(state, { payload }) {
      if (
        state.bankAccount &&
        payload.bankAccount &&
        payload.bankAccount.id == state.bankAccount.id
      ) {
        state.bankAccount = undefined;
      } else {
        state.bankAccount = payload.bankAccount;
      }
    },

    updateDeliveryNoteUpdateBillDataToUpdate(state, { payload }) {
      state.deliveryNote = payload.deliveryNote;
    },
    updateCustomerNoteToUpdate(state, { payload }) {
      state.customerNote = payload;
    },
    updateBillsUpdateBill(state, { payload }) {
      state.bills = payload;
    },
    updateSupplierNoteForUpdateBillToUpdate(state, { payload }) {
      state.supplierNote = payload;
    },
    updateSelectedProductsUpdateBill(state, { payload }) {
      const newSelectedProposalItems = payload;
      state.selectedProducts = newSelectedProposalItems;

      if (newSelectedProposalItems.length === 0) {
        state.summaryBill = initialSummaryBill;
      }
    },
    updateSelectedProductUpdateBill(
      state,
      {
        payload,
      }: {
        payload: {
          id: string;
          subID: string;
          quantity?: number;
          originPrice?: number;
        };
      }
    ) {
      const { id, subID, quantity, originPrice } = payload;
      const selectedProduct = state.selectedProducts.find(
        item => item.id == id && item.subID == subID
      );
      if (selectedProduct && quantity != undefined) {
        selectedProduct.customerQuantity = Number(quantity);
      }
      if (selectedProduct && originPrice != undefined) {
        selectedProduct.originPrice = Number(originPrice);
      }
      if (selectedProduct?.promotions && selectedProduct?.promotions.length) {
        selectedProduct.promotions.forEach(promotion => {
          if (promotion.type === PROMOTION_TYPE.RATE && promotion.rate > 0) {
            promotion.adjustment = Math.round(
              (selectedProduct.originPrice *
                selectedProduct.customerQuantity *
                promotion.rate) /
                100
            );
          } else if (
            promotion.type === PROMOTION_TYPE.AMOUNT &&
            promotion.amount > 0
          ) {
            const amount =
              promotion.amount >= selectedProduct.originPrice
                ? selectedProduct.originPrice
                : promotion.amount;

            promotion.adjustment = Math.round(
              amount * selectedProduct.customerQuantity
            );
          }
        });
      }
    },
    setChoiceProductListUpdateBill(state, { payload }) {
      state.choiceItemList = payload;
    },
    setReloadUpdateBillToUpdate(state, { payload }) {
      state.isLoading = payload;
    },
    updateShouldUpdateBillDataToUpdate(state, { payload }) {
      state.shouldUpdateBillData = payload;
    },
    revertStateWhenHasBeErrorUpdateBill(state) {
      state.selectedProducts = [...state.selectedProducts];
    },
    resetValidationResultUpdateBill: state => {
      state.validationResult = validationResultInitialState;
    },
    resetUpdateBillData(state) {
      handleResetBillData(state);
    },
  },
  extraReducers: builder => {
    builder.addCase(findBillToUpdate.fulfilled, (state, action) => {
      const { data } = action.payload;
      const {
        bill,
        summaryBill,
        selectedBillItems,
        totalCustomerQuantity,
        partnerID,
      } = data;

      const {
        customer,
        supplier,
        additionalInfo,
        paymentMethod,
        customerNote,
        supplierNote,
        billItems,
        customerID,
        chainCustomerID,
        deliveryMethod,
      } = bill;

      const billAddress = convertAdditionalInfoAddressToAddressData(
        additionalInfo.address
      );

      if (
        state.address &&
        state.address?.key?.includes('addition-address-bill')
      ) {
        state.addressData = state.addressData.filter(item => !item.notExisted);
      }
      state.address = billAddress
        ? {
            ...billAddress,
            disabledUpdateRemove: true,
            key: `${billAddress.id}addition-address-bill`,
          }
        : initialState.address;
      if (state.addressData.length > 0) {
        const foundAddressIndex = state.addressData.findIndex(
          item => item.id === billAddress?.id
        );
        const oldBillAddress = billAddress
          ? {
              ...billAddress,
              disabledUpdateRemove: true,
              key: `${billAddress.id}addition-address-bill`,
            }
          : undefined;
        if (
          oldBillAddress &&
          compareAddress(
            oldBillAddress,
            state.addressData[foundAddressIndex]
          ) == false
        ) {
          state.addressData = [
            { ...oldBillAddress, notExisted: true },
            ...state.addressData,
          ];
        } else if (foundAddressIndex != -1) {
          const newAddressList = [...state.addressData];
          newAddressList[foundAddressIndex].disabledUpdateRemove = true;
          state.addressData = newAddressList;
        }
      }

      state.entityPricePolicyID = data?.entityPricePolicyID;
      state.oldEntityPricePolicyID = data?.oldEntityPricePolicyID;
      state.entityProposalPolicyID = data?.entityProposalPolicyID;

      state.createdAt = bill?.createdAt;
      state.orderStatusUpdatedAt = bill?.order?.statusUpdatedAt;
      state.bill = bill;
      state.shouldCreateOrder = bill?.shouldCreateOrder;
      state.paymentMethod = paymentMethod;
      state.deliveryMethod = deliveryMethod;
      state.customerNote = customerNote;
      state.supplierNote = supplierNote;
      state.summaryBill = summaryBill;
      state.totalCustomerQuantity = totalCustomerQuantity;
      state.customerID = customerID;
      state.chainCustomerID = chainCustomerID;
      state.billID = bill.id;
      state.billCode = bill.code;
      state.partnerID = partnerID;
      state.deliveryFee = bill.deliveryFee;
      state.deliveryFeeType =
        bill?.deliveryFeeType ?? DELIVERY_FEE_TYPE.DEFAULT;
      state.promotions = summaryBill.adjustments
        .filter((item: any) => item.type == ADJUSTMENT_TYPE.PROMOTION)
        .map((item: any) => item.info);
      const findOrderSalePromotionCondition =
        summaryBill.orderSalePromotionConditions.find(
          (item: any) => item.isSelected
        );

      state.selectedOrderSalePromotionCondition =
        findOrderSalePromotionCondition;

      state.images = (bill?.billMedias ?? [])
        .filter((item: any) => item.type == 1)
        .map((mapItem: any) => {
          return { ...mapItem.media, uid: mapItem.media.id };
        });

      state.totalPrice = Number(summaryBill?.totalPrice ?? 0);
      state.depositMoney = summaryBill?.depositMoney ?? {};
      state.paidMoney = summaryBill?.paidMoney ?? {};
      state.returnMoney = Number(summaryBill?.returnMoney ?? 0);
      state.deductedMoney = Number(summaryBill?.deductedMoney ?? 0);
      state.excessMoney = -Number(summaryBill?.excessMoney ?? 0);
      state.isDebt = summaryBill?.isDebt;
      state.debtMoney = Number(summaryBill?.debtMoney ?? 0);

      state.customer = customer;
      state.supplier = supplier;

      state.bankAccount = bill?.additionalInfo?.bankAccount ?? undefined;
      state.deliveryNote = bill?.additionalInfo?.transportNote ?? undefined;

      state.selectedProducts = billItems?.map(item => {
        const findSelectedBillItem = selectedBillItems?.find(
          (selectedBillItem: any) => selectedBillItem.billItemID == item.id
        );

        const priceUnitList = findSelectedBillItem?.priceUnitList.map(
          (priceUnit: any) => {
            priceUnit.isSupplierSelected =
              priceUnit.id == findSelectedBillItem.priceUnit.id;
            return priceUnit;
          }
        );

        return {
          id: item.id,
          subID: generateUUID(),
          code: item?.supplierProduct?.code ?? item?.product?.code ?? '',
          billItemID: item.id,
          productID: item?.productID,
          name: item?.product?.name,
          avatar: item.product?.avatar,
          quantity: findSelectedBillItem?.customerQuantity,
          reportQuantity: findSelectedBillItem?.reportQuantity,
          productPricePolicyID: findSelectedBillItem?.productPricePolicyID,
          proposalItemID: findSelectedBillItem?.proposalItemID,
          price: item.price,
          priceUnit: {
            ...findSelectedBillItem?.priceUnit,
            isSupplierSelected: true,
          },
          priceUnitList,
          promotions: findSelectedBillItem?.promotions,
          supplierNote: item?.supplierNote || '',
          customerNote: item?.customerNote || '',
          currentPrice:
            findSelectedBillItem?.currentPrice ??
            {
              ...findSelectedBillItem?.priceUnit,
              isSupplierSelected: true,
            }.price,
          originPrice:
            findSelectedBillItem?.originPrice ??
            {
              ...findSelectedBillItem?.priceUnit,
              isSupplierSelected: true,
            }.price,
          customerQuantity: findSelectedBillItem?.customerQuantity,
          soldQuantity: item?.product?.soldQuantity,
          originCustomerQuantity: item?.customerQuantity,
          supplierProductOptions: item?.supplierProductOptions,
          salePromotionConditions:
            findSelectedBillItem?.salePromotionConditions ?? [],
          onHand: findSelectedBillItem?.onHand || 0,
          available: findSelectedBillItem?.available || 0,
          entityProductID: findSelectedBillItem?.entityProductID,
        };
      });

      state.isLoading = false;
    });

    builder.addCase(updateAddressForUpdateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      const foundAddressIndex = state.addressData.findIndex(
        item =>
          item.id === data?.id &&
          item.key &&
          !item.key?.includes('addition-address-bill')
      );
      if (foundAddressIndex != -1) {
        state.addressData[foundAddressIndex] = {
          ...state.addressData[foundAddressIndex],
          ...data,
          isSupplierCreateForPartner: true,
        };
      }

      if (
        state.address?.id === data?.id &&
        !state.address?.key?.includes('addition-address-bill')
      ) {
        state.address = {
          ...state.address,
          ...data,
        };
      }
    });
    builder.addCase(
      checkBillItemQuantityForUpdateBill.pending,
      (state, response) => {
        state.isCheckingBillItemQuantity = true;
      }
    );
    builder.addCase(
      checkBillItemQuantityForUpdateBill.rejected,
      (state, response) => {
        state.isCheckingBillItemQuantity = false;
      }
    );
    builder.addCase(
      checkBillItemQuantityForUpdateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const {
          summaryBill,
          validationResult,
          selectedProducts,
          totalQuantity,
        } = data;
        const { isUpdatingProductItem, body } = action.meta.arg;

        state.shouldUpdateBillData = !isUpdatingProductItem;
        state.entityPricePolicyID = body.entityPricePolicyID;
        state.oldEntityPricePolicyID = data?.oldEntityPricePolicyID;

        if (data.validationResult && !data.validationResult.isValid) {
          state.shouldUpdateBillData = true;
          state.validationResult = data.validationResult;
          updateItemsWithValidationResult(state);
          buildItems(state, selectedProducts);
        }

        if (!isUpdatingProductItem) {
          buildItems(state, selectedProducts);
        }

        const tempSelectedProducts = (selectedProducts ?? []).map(item => {
          if (item.billItemID) {
            const selectedItem = state?.selectedProducts?.find(
              productListItem => productListItem.billItemID == item.billItemID
            );

            if (selectedItem) {
              selectedItem.salePromotionConditions =
                item?.salePromotionConditions ?? [];

              return selectedItem;
            }
          }

          if (item.proposalItemID) {
            const selectedItem = state?.selectedProducts?.find(
              productListItem =>
                productListItem.proposalItemID == item.proposalItemID &&
                productListItem.subID == item.subID
            );

            if (selectedItem) {
              selectedItem.salePromotionConditions =
                item?.salePromotionConditions ?? [];

              return selectedItem;
            }
          }

          if (item.productID) {
            const selectedItem = state.selectedProducts.find(
              productItem =>
                productItem.productID == item.productID &&
                productItem.subID == item.subID
            );

            if (selectedItem) {
              selectedItem.salePromotionConditions =
                item?.salePromotionConditions ?? [];

              return selectedItem;
            }
          }

          return item;
        });

        state.selectedProducts = tempSelectedProducts;

        state.deliveryFee = summaryBill.deliveryFee ?? 0;
        state.deliveryFeeType =
          summaryBill.deliveryFeeDetail?.deliveryFeeType ??
          DELIVERY_FEE_TYPE.DEFAULT;
        state.validationResult = validationResult;
        state.summaryBill = summaryBill;
        state.totalCustomerQuantity = totalQuantity;
        state.shouldUpdateBillData = true;
        state.promotions = summaryBill.adjustments
          .filter(
            (item: any) =>
              item.type == ADJUSTMENT_TYPE.PROMOTION && item.price > 0
          )
          .map((item: any) => item.info);

        const findOrderSalePromotionCondition =
          summaryBill.orderSalePromotionConditions.find(
            (item: any) => item.isSelected
          );

        state.selectedOrderSalePromotionCondition =
          findOrderSalePromotionCondition;

        state.locationShippingFee =
          summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem;
        state.distanceShippingFee =
          summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee;
        state.customShippingFees = (state.customShippingFees || []).map(
          item => {
            const foundItem = (
              summaryBill?.deliveryFeeDetail?.customShippingFees || []
            ).find(item1 => item1.id == item.id);
            if (foundItem) {
              return {
                ...item,
                ...foundItem,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );
        state.shippingPolicyItems = (state.shippingPolicyItems || []).map(
          item => {
            if (
              summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem?.id ==
              item.id
            ) {
              return {
                ...item,
                ...summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );
        state.distanceShippingFees = (state.distanceShippingFees || []).map(
          item => {
            if (
              summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee?.id ==
              item.id
            ) {
              return {
                ...item,
                ...summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );

        state.totalPrice = Number(summaryBill?.totalPrice ?? 0);
        state.depositMoney = summaryBill?.depositMoney ?? {};
        state.paidMoney = summaryBill?.paidMoney ?? {};
        state.returnMoney = Number(summaryBill?.returnMoney ?? 0);
        state.deductedMoney = Number(summaryBill?.deductedMoney ?? 0);
        state.excessMoney = -Number(summaryBill?.excessMoney ?? 0);
        state.isDebt = summaryBill?.isDebt;
        state.debtMoney = Number(summaryBill?.debtMoney ?? 0);
        state.isCheckingBillItemQuantity = false;
      }
    );
    builder.addCase(updateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      const { summaryBill, validationResult, selectedProducts, totalQuantity } =
        data;
      if (data.validationResult && !data.validationResult.isValid) {
        state.deliveryFee = summaryBill.deliveryFee;
        state.deliveryFeeType = summaryBill.deliveryFeeDetail?.deliveryFeeType;
        state.summaryBill = summaryBill;
        state.validationResult = validationResult;
        state.totalCustomerQuantity = totalQuantity;
        state.promotions = summaryBill.adjustments
          .filter((item: any) => item.type == ADJUSTMENT_TYPE.PROMOTION)
          .map((item: any) => item.info);

        state.locationShippingFee =
          summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem;
        state.distanceShippingFee =
          summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee;
        state.customShippingFees = (state.customShippingFees || []).map(
          item => {
            const foundItem = (
              summaryBill?.deliveryFeeDetail?.customShippingFees || []
            ).find(item1 => item1.id == item.id);
            if (foundItem) {
              return {
                ...item,
                ...foundItem,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );
        state.shippingPolicyItems = (state.shippingPolicyItems || []).map(
          item => {
            if (
              summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem?.id ==
              item.id
            ) {
              return {
                ...item,
                ...summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );
        state.distanceShippingFees = (state.distanceShippingFees || []).map(
          item => {
            if (
              summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee?.id ==
              item.id
            ) {
              return {
                ...item,
                ...summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );

        state.totalPrice = Number(summaryBill?.totalPrice ?? 0);
        state.depositMoney = summaryBill?.depositMoney ?? {};
        state.paidMoney = summaryBill?.paidMoney ?? {};
        state.returnMoney = Number(summaryBill?.returnMoney ?? 0);
        state.deductedMoney = Number(summaryBill?.deductedMoney ?? 0);
        state.excessMoney = -Number(summaryBill?.excessMoney ?? 0);
        state.isDebt = summaryBill?.isDebt;
        state.debtMoney = Number(summaryBill?.debtMoney ?? 0);

        buildItems(state, selectedProducts);
        updateItemsWithValidationResult(state);
        state.shouldUpdateBillData = true;
      } else {
        handleResetBillData(state);
      }
    });

    builder.addCase(createAddressForUpdateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      const newAddress = {
        ...data,
        key: data.id,
      };
      state.addressData = [...state.addressData, newAddress];
    });
    builder.addCase(findListAddressForUpdateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      if (data?.length > 0) {
        const keyAddressData = data.map((item: any) => {
          return { ...item, key: item.id };
        });
        const foundAddressIndex = keyAddressData.findIndex(
          item => item.id == state.address?.id
        );
        const oldBillAddress = state.address
          ? {
              ...state.address,
              disabledUpdateRemove: true,
            }
          : undefined;
        if (foundAddressIndex === -1) {
          if (oldBillAddress) {
            state.addressData = [oldBillAddress, ...keyAddressData];
          } else {
            state.addressData = [...keyAddressData];
          }
        } else {
          if (state.address) {
            if (
              compareAddress(
                state.address,
                keyAddressData[foundAddressIndex]
              ) == false
            ) {
              state.addressData = [
                { ...oldBillAddress, notExisted: true },
                ...keyAddressData,
              ];
            } else {
              const newAddressList = [...keyAddressData];
              newAddressList[foundAddressIndex].disabledUpdateRemove = true;
              state.addressData = newAddressList;
            }
          }
        }
      } else if (state.address) {
        state.addressData = [state.address];
      }
    });
    builder.addCase(
      findEntityCustomShippingFeeToUpdateBill.pending,
      (state, response) => {
        state.isLoadingCustomShippingFees = true;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToUpdateBill.rejected,
      (state, response) => {
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToUpdateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.customShippingFees = data?.map(item => {
          item.isSelected = item.billID != undefined;
          return item;
        });
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(removeAddressForUpdateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      if (foundAddressIndex !== -1) {
        state.addressData.splice(foundAddressIndex, 1);
      }
    });
    builder.addCase(
      findEntityDistanceShippingFeeToUpdateBill.pending,
      (state, response) => {
        state.isLoadingDistanceShippingFees = true;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToUpdateBill.rejected,
      (state, response) => {
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToUpdateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.distanceShippingFees = data?.map(item => {
          item.isSelected = item.billID != undefined;
          if (item.isSelected) {
            state.distanceShippingFee = item;
          }
          return item;
        });
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToUpdateBill.pending,
      (state, response) => {
        state.isLoadingShippingPolicyItems = true;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToUpdateBill.rejected,
      (state, response) => {
        state.isLoadingShippingPolicyItems = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToUpdateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.shippingPolicyItems = data?.map(item => {
          item.isSelected = item.billID != undefined;
          if (item.isSelected) {
            state.locationShippingFee = item;
          }
          return item;
        });
        state.isLoadingShippingPolicyItems = false;
      }
    );
  },
});

const updateItemsWithValidationResult = (state: IState) => {
  const {
    deletedBillItemIDs,
    deletedProposalItemIDs,
    isProposalItemDeleted,
    isProposalItemNotEnoughQuantity,
    isPriceChanged,
    notEnoughQuantityProposalItems,
    priceChangedBillItems,
    priceChangedProductItems,
    notEnoughQuantityBillItems,
    isProductNotEnoughQuantity,
    notEnoughQuantityProducts,
    isProductPriceChanged,
    isProductDeactivated,
    deactivatedProductIDs,
    isAddressChanged,
    changedAddress,
    priceChangedProducts,
    isBillEntityShippingPolicyItemChanged,
    changedBillEntityShippingPolicyItem,
    isBillEntityDistanceShippingFeeChanged,
    changedBillEntityDistanceShippingFee,
    isBillEntityCustomShippingFeesChanged,
    changedBillEntityCustomShippingFees,
  } = state.validationResult;

  let selectedProducts = [...state.selectedProducts];

  if (isProposalItemDeleted) {
    selectedProducts = selectedProducts?.filter(
      item =>
        !(item.billItemID && deletedBillItemIDs?.includes(item.billItemID))
    );
  }

  if (isProposalItemNotEnoughQuantity) {
    selectedProducts.forEach(item => {
      const notEnoughQuantityProposalItem =
        notEnoughQuantityProposalItems?.find(
          notEnoughQuantityItem =>
            notEnoughQuantityItem.id === item?.proposalItemID &&
            notEnoughQuantityItem.subID === item?.subID
        );

      if (notEnoughQuantityProposalItem) {
        item.customerQuantity =
          notEnoughQuantityProposalItem.newCustomerQuantity;
      }

      if (item.billItemID) {
        const notEnoughQuantityBillItem = notEnoughQuantityBillItems?.find(
          notEnoughQuantityItem => notEnoughQuantityItem.id === item.billItemID
        );

        if (notEnoughQuantityBillItem) {
          item.customerQuantity = notEnoughQuantityBillItem.newCustomerQuantity;
        }
      }
    });
  }

  if (isProductNotEnoughQuantity) {
    selectedProducts.forEach(item => {
      const notEnoughQuantityProposalItem = notEnoughQuantityProducts?.find(
        notEnoughQuantityProduct =>
          notEnoughQuantityProduct.id === item?.productID &&
          notEnoughQuantityProduct.subID === item?.subID
      );

      if (notEnoughQuantityProposalItem) {
        item.customerQuantity =
          notEnoughQuantityProposalItem.newCustomerQuantity;
      }
    });
  }

  if (isProductDeactivated) {
    selectedProducts.forEach(item => {
      if (item?.productID) {
        const notEnoughQuantityProposalItem: string =
          deactivatedProductIDs?.find(
            deactivatedProductID => deactivatedProductID == item?.productID
          );

        if (notEnoughQuantityProposalItem) {
          selectedProducts = selectedProducts.filter(selectedItem => {
            if (selectedItem?.productID) {
              if (selectedItem?.productID != notEnoughQuantityProposalItem) {
                return true;
              }
            }

            return false;
          });
        }
      }
    });
  }

  if (
    isPriceChanged &&
    (priceChangedProducts?.length > 0 || priceChangedBillItems?.length > 0)
  ) {
    selectedProducts.forEach(item => {
      if (priceChangedProducts?.length > 0) {
        const priceChangedProduct = priceChangedProducts.find(
          priceChangedItem =>
            priceChangedItem.id === item.id &&
            priceChangedItem.subID === item.subID
        );

        if (priceChangedProduct) {
          if (
            priceChangedProduct.areAllPriceUnitsDeleted ||
            priceChangedProduct.isOldPriceUnitDeleted ||
            priceChangedProduct.newPriceUnitList?.length
          ) {
            item.priceUnitList = priceChangedProduct.newPriceUnitList.filter(
              priceUnit => priceUnit.isSelected
            );
            item.priceUnitList = item.priceUnitList.map(priceUnit => {
              priceUnit.isSupplierSelected =
                priceUnit.id == priceChangedProduct.newPriceUnit.id;
              return priceUnit;
            });
          } else {
            const indexPriceUnit = item.priceUnitList.findIndex(
              priceUnit => priceUnit.id == priceChangedProduct.newPriceUnit.id
            );
            if (indexPriceUnit == -1) {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                priceUnit.isSupplierSelected = false;
                return priceUnit;
              });
              item.priceUnitList.push({
                ...priceChangedProduct.newPriceUnit,
                isSupplierSelected: true,
              });
            } else {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                if (priceUnit.id == priceChangedProduct.newPriceUnit.id) {
                  return {
                    ...priceUnit,
                    ...priceChangedProduct.newPriceUnit,
                    isSupplierSelected: true,
                  };
                }
                return {
                  ...priceUnit,
                  isSupplierSelected: false,
                };
              });
            }
          }

          item.priceUnit = priceChangedProduct.newPriceUnit;
          item.customerQuantity = priceChangedProduct.newCustomerQuantity;
          if (priceChangedProduct.currentPrice) {
            item.originPrice = priceChangedProduct.currentPrice;
          }
          if (priceChangedProduct.originPrice) {
            item.originPrice = priceChangedProduct.originPrice;
          }
          item.productPricePolicyID = priceChangedProduct.productPricePolicyID;
          item.proposalItemID = priceChangedProduct.proposalItemID;

          state.choiceItemList = state.choiceItemList.map(choiceItem => {
            if (choiceItem.id == item.id && choiceItem.subID == item.subID) {
              choiceItem.priceUnit = item.priceUnit;
              choiceItem.priceUnitList = item.priceUnitList;
            }
            return choiceItem;
          });
        }
      }

      if (priceChangedBillItems?.length > 0 && item.billItemID) {
        const priceChangedBillItem = priceChangedBillItems?.find(
          priceChangedItem => priceChangedItem.id === item.billItemID
        );

        if (priceChangedBillItem) {
          if (
            priceChangedBillItem.areAllPriceUnitsDeleted ||
            priceChangedBillItem.isOldPriceUnitDeleted ||
            priceChangedBillItem.newPriceUnitList?.length
          ) {
            item.priceUnitList = priceChangedBillItem.newPriceUnitList.filter(
              priceUnit => priceUnit.isSelected
            );
            item.priceUnitList = item.priceUnitList.map(priceUnit => {
              priceUnit.isSupplierSelected =
                priceUnit.id == priceChangedBillItem.newPriceUnit.id;
              return priceUnit;
            });
          } else {
            const indexPriceUnit = item.priceUnitList.findIndex(
              priceUnit => priceUnit.id == priceChangedBillItem.newPriceUnit.id
            );
            if (indexPriceUnit == -1) {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                priceUnit.isSupplierSelected = false;
                return priceUnit;
              });
              item.priceUnitList.push({
                ...priceChangedBillItem.newPriceUnit,
                isSupplierSelected: true,
              });
            } else {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                if (priceUnit.id == priceChangedBillItem.newPriceUnit.id) {
                  return {
                    ...priceUnit,
                    ...priceChangedBillItem.newPriceUnit,
                    isSupplierSelected: true,
                  };
                }
                return {
                  ...priceUnit,
                  isSupplierSelected: false,
                };
              });
            }
          }
          item.priceUnit = priceChangedBillItem.newPriceUnit;
          item.customerQuantity = priceChangedBillItem.newCustomerQuantity;
          if (priceChangedBillItem.currentPrice) {
            item.originPrice = priceChangedBillItem.currentPrice;
          }
          if (priceChangedBillItem.originPrice) {
            item.originPrice = priceChangedBillItem.originPrice;
          }
          item.productPricePolicyID = priceChangedBillItem.productPricePolicyID;
          item.proposalItemID = priceChangedBillItem.proposalItemID;
        }
      }
    });
  }

  if (isProductPriceChanged) {
    selectedProducts.forEach(item => {
      const priceChangedProduct = priceChangedProducts?.find(
        priceChangedItem =>
          priceChangedItem.id === item?.productID &&
          priceChangedItem.subID === item?.subID
      );

      if (priceChangedProduct) {
        item.priceUnit = priceChangedProduct.newPriceUnit;
        item.customerQuantity = priceChangedProduct.newCustomerQuantity;

        const indexPriceUnit = item?.priceUnitList?.findIndex(
          ele => ele.id == priceChangedProduct.newPriceUnit.id
        );

        if (indexPriceUnit == -1) {
          item.priceUnitList = [
            ...item?.priceUnitList?.map(ele => {
              ele.isSupplierSelected = false;
              return ele;
            }),
            { ...priceChangedProduct.newPriceUnit, isSupplierSelected: true },
          ];
        } else {
          item.priceUnitList = item?.priceUnitList?.map(ele => {
            let temp = ele;
            if (ele.id == priceChangedProduct.newPriceUnit.id) {
              temp = {
                ...temp,
                ...priceChangedProduct.newPriceUnit,
                isSupplierSelected: true,
              };
            } else {
              if (ele.id == priceChangedProduct.oldPriceUnit.id) {
                temp = {
                  ...temp,
                  ...priceChangedProduct.oldPriceUnit,
                  isSupplierSelected: false,
                };
              } else {
                temp.isSupplierSelected = false;
              }
            }

            return temp;
          });
        }

        if (priceChangedProduct?.isOldPriceUnitDeleted) {
          item.priceUnitList = item?.priceUnitList?.filter(
            ele => ele.id != priceChangedProduct.oldPriceUnit.id
          );
        }
      }
    });
  }

  if (isBillEntityShippingPolicyItemChanged) {
    state.shippingPolicyItems =
      changedBillEntityShippingPolicyItem.entityShippingPolicyItems;
    state.locationShippingFee =
      changedBillEntityShippingPolicyItem.newBillEntityShippingPolicyItem;
    state.shippingPolicyItems?.forEach(item => {
      item.isSelected = state.locationShippingFee.id == item.id;
    });
  }

  if (isBillEntityDistanceShippingFeeChanged) {
    state.distanceShippingFees =
      changedBillEntityDistanceShippingFee.entityDistanceShippingFees;
    state.distanceShippingFee =
      changedBillEntityDistanceShippingFee.newBillEntityDistanceShippingFee;
    state.distanceShippingFees?.forEach(item => {
      item.isSelected = state.distanceShippingFee.id == item.id;
    });
  }

  if (isBillEntityCustomShippingFeesChanged) {
    state.customShippingFees =
      changedBillEntityCustomShippingFees.entityCustomShippingFees;
  }

  if (isAddressChanged && changedAddress) {
    let isSelectedAddress = false;
    const foundAddressIndex = state.addressData.findIndex(
      item => item.id === changedAddress.id
    );
    if (foundAddressIndex !== -1) {
      isSelectedAddress = state.addressData[foundAddressIndex].isSelected;
      state.addressData[foundAddressIndex] = {
        ...state.addressData[foundAddressIndex],
        ...changedAddress,
        ward: {
          id: changedAddress.ward?.id,
          code: changedAddress.ward?.code,
          fullName: changedAddress.ward?.fullName,
          district: changedAddress.district,
          province: changedAddress.province,
        },
        district: changedAddress.district,
        province: changedAddress.province,
        isSelected: isSelectedAddress,
      };
    }
    state.address = {
      ...state.address,
      ...changedAddress,
      ward: {
        id: changedAddress.ward.id,
        code: changedAddress.ward?.code,
        fullName: changedAddress.ward?.fullName,
        district: changedAddress.district,
        province: changedAddress.province,
      },
      district: changedAddress.district,
      province: changedAddress.province,
      isSelected: isSelectedAddress,
    };
  }
  state.selectedProducts = selectedProducts;
};

const handleResetBillData = (state: IState) => {
  state.selectedProducts = initialState.selectedProducts;
  state.validationResult = initialState.validationResult;
  state.customerID = initialState.customerID;
  state.chainCustomerID = initialState.chainCustomerID;
  state.billID = initialState.billID;
  state.billCode = initialState.billCode;
  state.deliveryMethod = initialState.deliveryMethod;
  state.paymentMethod = initialState.paymentMethod;
  state.address = initialState.address;
  state.addressData = initialState.addressData;
  state.customer = initialState.customer;
  state.supplier = initialState.supplier;
  state.totalCustomerQuantity = initialState.totalCustomerQuantity;
  state.entityPricePolicyID = initialState.entityPricePolicyID;
  state.oldEntityPricePolicyID = initialState.oldEntityPricePolicyID;

  state.totalPrice = 0;
  state.returnMoney = 0;
  state.paidMoney = {};
  state.depositMoney = {};
  state.deductedMoney = 0;
  state.excessMoney = 0;
  state.isDebt = false;
  state.debtMoney = 0;

  state.promotions = [];
  state.addQuantityValue = 1;
  state.shippingPolicyItems = [];
  state.distanceShippingFees = [];
  state.customShippingFees = [];
  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.isCheckingBillItemQuantity = false;
  state.isLoadingShippingPolicyItems = false;
  state.isLoadingDistanceShippingFees = false;
  state.isLoadingCustomShippingFees = false;
  state.summaryBill = initialSummaryBill;

  state.shouldCreateOrder = initialState.shouldCreateOrder;
};

const buildItems = async (state: IState, selectedItemList: any[]) => {
  try {
    const selectedProducts = selectedItemList?.map(item => {
      if (item.billItemID) {
        const selectedItem = state?.selectedProducts?.find(
          productListItem => productListItem.billItemID == item.billItemID
        );

        if (selectedItem) {
          let priceUnitList = item.priceUnitList ?? selectedItem.priceUnitList;
          priceUnitList = priceUnitList
            .filter(item => item.isSelected)
            .map(priceUnit => {
              return {
                ...priceUnit,
                isSupplierSelected: item.priceUnit.id === priceUnit.id,
              };
            });

          selectedItem.priceUnit = item.priceUnit;
          selectedItem.priceUnitList = priceUnitList;

          selectedItem.reportQuantity = item.reportQuantity;
          selectedItem.customerQuantity = item.customerQuantity;
          selectedItem.currentPrice =
            item.currentPrice ?? item.priceUnit.price ?? 0;
          selectedItem.originPrice =
            item.originPrice ?? item.priceUnit.price ?? 0;
          selectedItem.promotions = item.promotions ?? [];
          selectedItem.proposalItemID = item.proposalItemID;
          selectedItem.productPricePolicyID = item.productPricePolicyID;
          selectedItem.salePromotionConditions =
            item?.salePromotionConditions ?? [];

          return selectedItem;
        }
      }

      if (item.proposalItemID) {
        const selectedItem = state?.selectedProducts?.find(
          productListItem =>
            productListItem.proposalItemID == item.proposalItemID &&
            productListItem.subID == item.subID
        );

        if (selectedItem) {
          let priceUnitList = item.priceUnitList ?? selectedItem.priceUnitList;
          priceUnitList = priceUnitList
            .filter(item => item.isSelected)
            .map(priceUnit => {
              return {
                ...priceUnit,
                isSupplierSelected: item.priceUnit.id === priceUnit.id,
              };
            });

          selectedItem.billItemID = item.billItemID;
          selectedItem.priceUnit = item.priceUnit;
          selectedItem.priceUnitList = priceUnitList;
          selectedItem.reportQuantity = item.reportQuantity;
          selectedItem.customerQuantity = item.customerQuantity;
          selectedItem.currentPrice =
            item.currentPrice ?? item.priceUnit.price ?? 0;
          selectedItem.originPrice =
            item.originPrice ?? item.priceUnit.price ?? 0;
          selectedItem.promotions = item.promotions ?? [];
          selectedItem.proposalItemID = item.proposalItemID;
          selectedItem.productPricePolicyID = item.productPricePolicyID;
          selectedItem.salePromotionConditions =
            item?.salePromotionConditions ?? [];
          selectedItem.onHand = item?.onHand || 0;
          selectedItem.available = item?.available || 0;
          selectedItem.entityProductID = item?.supplierEntityProductID;

          return selectedItem;
        }
      }

      if (item.productID) {
        const selectedItem = state.selectedProducts.find(
          productItem =>
            productItem.productID == item.productID &&
            productItem.subID == item.subID
        );

        if (selectedItem) {
          let priceUnitList = item.priceUnitList ?? selectedItem.priceUnitList;
          priceUnitList = priceUnitList
            .filter(item => item.isSelected)
            .map(priceUnit => {
              return {
                ...priceUnit,
                isSupplierSelected: item.priceUnit.id === priceUnit.id,
              };
            });

          selectedItem.billItemID = item.billItemID;
          selectedItem.priceUnit = item.priceUnit;
          selectedItem.priceUnitList = priceUnitList;
          selectedItem.reportQuantity = item.reportQuantity;
          selectedItem.currentPrice =
            item.currentPrice ?? item.priceUnit.price ?? 0;
          selectedItem.originPrice =
            item.originPrice ?? item.priceUnit.price ?? 0;
          selectedItem.customerQuantity = item.customerQuantity;
          selectedItem.promotions = item.promotions ?? [];
          selectedItem.proposalItemID = item.proposalItemID;
          selectedItem.productPricePolicyID = item.productPricePolicyID;
          selectedItem.salePromotionConditions =
            item?.salePromotionConditions ?? [];
          selectedItem.onHand = item?.onHand || 0;
          selectedItem.available = item?.available || 0;
          selectedItem.entityProductID = item?.supplierEntityProductID;

          return selectedItem;
        }
      }

      return item;
    });

    state.selectedProducts = selectedProducts;
  } catch (error: any) {
    console.log('Error while building items (update bill)', error);
  }
};

const { actions, reducer: UpdateBillReducer } = updateBillSlice;
export default UpdateBillReducer;
export const {
  updateBillInfoToUpdateBill,
  updateBillsUpdateBill,
  updateCustomerNoteToUpdate,
  updateSelectedProductsUpdateBill,
  updateSelectedProductUpdateBill,
  setChoiceProductListUpdateBill,
  setReloadUpdateBillToUpdate,
  revertStateWhenHasBeErrorUpdateBill,
  updateShouldUpdateBillDataToUpdate,
  updateSupplierNoteForUpdateBillToUpdate,
  resetValidationResultUpdateBill,
  resetUpdateBillData,
  updateBankAccountInfoUpdateBillDataToUpdate,
  updateDeliveryNoteUpdateBillDataToUpdate,
} = actions;

export const selectUpdateBill = (state: RootState): IState =>
  state.updateBill as IState;
